<template>
  <!-- Match RangeSelector: label + period controls sit outside .weekly-budget so mobile
       horizontal inset applies only to the slider (aligns "Budget" + pills with "Price (RRP)"). -->
  <div class="range-selector-budget" :class="{ 'range-selector-budget--pill-drawer': pillPeriodToggle }">
    <p v-if="label" class="range-selector-budget__section-label font-nunito text-weight-bolder primary-text">{{ label }}</p>

    <!-- Mobile drawer: pill toggle (matches Sort / Payments pattern) -->
    <div v-if="pillPeriodToggle" class="row q-col-gutter-sm no-wrap items-stretch range-selector-budget__period-pills">
      <div class="col">
        <CalculatorButton
          label="Weekly"
          rounded
          outlined
          remove-opacity
          type="button"
          class="range-selector-budget__period-btn full-width"
          :color="innerPeriod === REPAYMENT_WEEKLY ? 'primary' : undefined"
          :disabled="disabled"
          @click="setPeriod(REPAYMENT_WEEKLY)"
        />
      </div>
      <div class="col">
        <CalculatorButton
          label="Monthly"
          rounded
          outlined
          remove-opacity
          type="button"
          class="range-selector-budget__period-btn full-width"
          :color="innerPeriod === REPAYMENT_MONTHLY ? 'primary' : undefined"
          :disabled="disabled"
          @click="setPeriod(REPAYMENT_MONTHLY)"
        />
      </div>
    </div>

    <!-- Desktop / default: text toggle -->
    <div v-else class="period-toggle">
      <span
        class="period-option"
        :class="{ 'period-option--active': innerPeriod === REPAYMENT_WEEKLY }"
        @click="setPeriod(REPAYMENT_WEEKLY)"
      >
        Weekly Budget
      </span>
      <span class="period-separator">/</span>
      <span
        class="period-option"
        :class="{ 'period-option--active': innerPeriod === REPAYMENT_MONTHLY }"
        @click="setPeriod(REPAYMENT_MONTHLY)"
      >
        Monthly Budget
      </span>
    </div>

    <div class="weekly-budget" :class="{ 'weekly-budget--pill-period': pillPeriodToggle }">
      <div class="md:w-90 md:m-auto">
        <QRange
          v-model="rangeModel"
          :min="rangeMin"
          :max="rangeMax"
          :step="rangeStep"
          :marker-labels="rangeMarkerLabel"
          :disable="disabled"
        />
      </div>

      <QIcon :name="closeIcon" class="q-field__marginal" size="20px" @click="close" v-if="rangeModel && rangeModel.min !== null" />
    </div>
  </div>
</template>

<script>
import { QRange, QIcon } from 'quasar';
import { evaCloseCircleOutline } from '@quasar/extras/eva-icons';
import { REPAYMENT_OPTIONS, REPAYMENT_MONTHLY, REPAYMENT_WEEKLY } from '../../../utils/constants';
import CalculatorButton from '../CalculatorButton';

export default {
  name: 'RangeSelectorBudget',
  components: { QRange, QIcon, CalculatorButton },

  props: {
    /** Section title (e.g. "Budget") — shown above period control when set */
    label: {
      type: String,
      default: ''
    },
    /** Mobile layout: Weekly / Monthly as full-width pill buttons */
    pillPeriodToggle: {
      type: Boolean,
      default: false
    },
    // v-model (value is interpreted by budgetType outside)
    value: {
      default: () => ({ min: null, max: 100 })
    },

    // WEEKLY slider config
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 1000
    },
    step: {
      type: Number,
      default: 100
    },
    markerLabel: {
      default: () => [],
      required: false
    },

    // MONTHLY slider config
    monthlyMin: {
      type: Number,
      default: 0
    },
    monthlyMax: {
      type: Number,
      default: 3500
    },
    monthlyStep: {
      type: Number,
      default: 500
    },
    // how often to show labels (to avoid overlap)
    monthlyLabelStep: {
      type: Number,
      default: 500
    },
    monthlyMarkerLabel: {
      type: [Object, Array, Function],
      default: null
    },

    disabled: {
      type: Boolean,
      default: false
    },

    // second "model" for Weekly / Monthly
    period: {
      type: String,
      default: REPAYMENT_WEEKLY,
      validator: (v) => REPAYMENT_OPTIONS.includes(v)
    }
  },

  data() {
    return {
      innerPeriod: this.period,
      closeIcon: evaCloseCircleOutline,
      REPAYMENT_WEEKLY,
      REPAYMENT_MONTHLY
    };
  },

  computed: {
    // Slider config per period
    rangeMin() {
      return this.innerPeriod === REPAYMENT_WEEKLY ? this.min : this.monthlyMin;
    },
    rangeMax() {
      return this.innerPeriod === REPAYMENT_WEEKLY ? this.max : this.monthlyMax;
    },
    rangeStep() {
      return this.innerPeriod === REPAYMENT_WEEKLY ? this.step : this.monthlyStep;
    },

    // Marker labels per period
    rangeMarkerLabel() {
      if (this.innerPeriod === REPAYMENT_WEEKLY) {
        return this.markerLabel;
      }

      if (this.monthlyMarkerLabel) {
        return this.monthlyMarkerLabel;
      }

      const labels = {};
      for (let v = this.monthlyMin; v <= this.monthlyMax; v += this.monthlyLabelStep) {
        labels[v] = `$${v}`;
      }
      return labels;
    },

    // QRange bridge – just proxies value, but ensures it's clamped to current range
    rangeModel: {
      get() {
        const val = this.value || { min: null, max: this.rangeMax };

        const clamp = (x) => {
          if (x == null) return null;
          return Math.min(this.rangeMax, Math.max(this.rangeMin, x));
        };

        return {
          min: val.min == null ? null : clamp(val.min),
          max: clamp(val.max)
        };
      },
      set(newVal) {
        this.$emit('input', newVal || { min: null, max: this.rangeMax });
      }
    }
  },

  watch: {
    period(newVal) {
      this.innerPeriod = newVal;
    },

    innerPeriod(newVal, oldVal) {
      // notify parent
      this.$emit('update:period', newVal);

      // when switching modes, clamp existing value to new range
      const current = this.value || { min: null, max: this.rangeMax };
      const clamp = (x) => {
        if (x == null) return null;
        return Math.min(this.rangeMax, Math.max(this.rangeMin, x));
      };

      const adjusted = {
        min: current.min == null ? null : clamp(current.min),
        max: clamp(current.max)
      };

      // only emit if something actually changed
      if (adjusted.min !== current.min || adjusted.max !== current.max) {
        this.$emit('input', adjusted);
      }
    }
  },

  methods: {
    setPeriod(p) {
      if (this.disabled || this.innerPeriod === p) {
        return;
      }
      this.innerPeriod = p;
    },

    close() {
      const reset = {
        min: null,
        max: this.rangeMin
      };
      this.$emit('input', reset);
    }
  }
};
</script>

<style lang="sass" scoped>
.range-selector-budget
  box-sizing: border-box
  // Was outer .weekly-budget padding-top before label lived outside slider wrapper
  padding-top: 10px

// Pill layout (mobile drawer): flush under Price block; spacing from parent margins
.range-selector-budget--pill-drawer
  padding-top: 0

.weekly-budget
  position: relative
  padding-top: 0
  font-size: 11px
  box-sizing: border-box

  .q-icon
    position: absolute
    top: 6px
    right: 0px

.weekly-budget--pill-period
  .q-icon
    top: -2px

.range-selector-budget__section-label
  font-size: 18px
  line-height: 150%
  margin: 0 0 12px

.range-selector-budget__period-pills
  margin-bottom: 16px

  .col
    min-width: 0

.range-selector-budget__period-btn.calculator-button.q-btn
  width: 100%

.period-toggle
  margin-bottom: 8px

.period-option
  cursor: pointer
  font-size: 13px
  color: var(--primary-color)
  font-weight: 400

.period-option--active
  font-weight: 600
  color: var(--primary-color)

.period-separator
  margin: 0 6px
  color: #9ca3af

@media screen and (max-width: 767px)
  .weekly-budget
    padding-left: 14px
    padding-right: 14px

@media screen and (min-width: 768px)
  .weekly-budget
    padding-left: 0
    padding-right: 0
  .md\:w-90
    width: 90%
  .md\:m-auto
    margin-left: calc(5% - 7px)
</style>
